import { FORUM_URL } from './menu.config'

/**
 * 포럼(Discourse) 로그인 연동 — judahsWeb/FORUM_PLAN.md §11.
 * 여기 세 가지를 `/sso` 라우트·로그인 사가·GNB/푸터/드로어/`/forum` 리다이렉트가 같이 본다.
 */

/**
 * 이 사이트의 로그인 토큰. 고객(`sessionStorage.accessCustomerToken`)이 먼저고, 없으면
 * 스태프(백오피스 로그인, `localStorage.accessToken`)다. 둘 다 있으면 고객이다 — 포럼은
 * 고객의 자리라서. 아무것도 없으면 ''.
 */
export const pickSiteToken = (): string => {
	try {
		const customerToken: string = sessionStorage.getItem('accessCustomerToken') ?? ''
		if (customerToken !== '') return customerToken

		const isStaffLogin: boolean = localStorage.getItem('isLogin') === 'true'
		return isStaffLogin ? localStorage.getItem('accessToken') ?? '' : ''
	} catch (error) {
		return ''
	}
}

/**
 * `pickSiteToken()` 이 고른 세션을 버린다 — API 가 그 토큰을 401 로 거절했을 때.
 * 흔한 경우는 2026-09-10 이전에 발급돼 `type` 이 없는 구 토큰이다. 지우지 않으면 로그인 페이지의
 * 가드가 "이미 로그인됨" 으로 보고 홈으로 튕겨서, 사용자는 영원히 재로그인 기회를 못 얻는다.
 */
export const dropSiteToken = (): void => {
	try {
		const customerToken: string = sessionStorage.getItem('accessCustomerToken') ?? ''
		if (customerToken !== '') {
			sessionStorage.setItem('accessCustomerToken', '')
			sessionStorage.setItem('refreshCustomerToken', '')
			return
		}
		localStorage.setItem('isLogin', 'false')
		localStorage.setItem('accessToken', '')
		localStorage.setItem('refreshToken', '')
	} catch (error) {
		// 저장소가 막힌 환경 — 지울 것도 없다
	}
}

/**
 * 사이트 세션을 **둘 다** 버린다(고객·스태프). `dropSiteToken()` 은 `pickSiteToken()` 이 고른
 * 한쪽만 지우지만, 포럼에서 로그아웃하고 돌아온 경우엔 남은 쪽이 있으면 다음 Login 클릭에서
 * 그 계정으로 그냥 다시 들어가 버린다 — 계정을 고를 기회가 사라진다.
 */
export const clearSiteSession = (): void => {
	try {
		sessionStorage.setItem('accessCustomerToken', '')
		sessionStorage.setItem('refreshCustomerToken', '')
		localStorage.setItem('isLogin', 'false')
		localStorage.setItem('accessToken', '')
		localStorage.setItem('refreshToken', '')
	} catch (error) {
		// 저장소가 막힌 환경 — 지울 것도 없다
	}
}

/**
 * 포럼으로 들어가는 주소. 이 사이트에 로그인돼 있으면 Discourse 의 `/session/sso` 로 보내
 * DiscourseConnect 를 바로 태운다 — 포럼에서 Login 을 누를 필요 없이 로그인된 채로 착지한다.
 * 세션이 없으면 그냥 포럼이다. (Discourse 쪽 `enable_discourse_connect` 가 꺼져 있으면
 * `/session/sso` 는 404 라서, 그 설정과 같은 배포에 켠다.)
 */
export const forumEntryUrl = (returnPath = '/'): string =>
	pickSiteToken() !== ''
		? `${FORUM_URL}/session/sso?return_path=${encodeURIComponent(returnPath)}`
		: FORUM_URL

/**
 * 로그인 뒤 돌아갈 사이트 안 경로를 두는 sessionStorage 키. `/sso` 가 적고 로그인·가입 사가가 읽는다.
 * 사이트 안 경로(`/`로 시작)만 유효하다 — 밖 주소를 허용하면 오픈 리다이렉트가 된다.
 */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'
